import { computed, onBeforeUnmount, ref, unref, watch } from "vue";
import { useRoute } from "vue-router";
import i18n from "@/locales/index.js";

export const APP_TITLE = "Mindsetech";

function toDisplayText(value) {
  return String(value || "").trim();
}

export function resolveRoutePageTitle(route) {
  const titleKey = route.meta?.title;
  if (!titleKey) return "";

  const pageTitle = i18n.global.t(titleKey);
  return pageTitle && pageTitle !== titleKey ? pageTitle : "";
}

export function formatPageTitle(pageTitle, { dirty = false } = {}) {
  const normalizedTitle = toDisplayText(pageTitle);
  const title = normalizedTitle ? `${normalizedTitle} - ${APP_TITLE}` : APP_TITLE;
  return dirty ? `* ${title}` : title;
}

function serializeForm(value) {
  return JSON.stringify(unref(value));
}

export function useFormDirty(source) {
  let initialSnapshot = "";
  const ready = ref(false);

  const isDirty = computed(() => ready.value && serializeForm(source) !== initialSnapshot);

  function markClean() {
    initialSnapshot = serializeForm(source);
    ready.value = true;
  }

  function pauseDirtyTracking() {
    ready.value = false;
  }

  return {
    isDirty,
    markClean,
    pauseDirtyTracking
  };
}

export function usePageTitle(pageTitle, options = {}) {
  const route = useRoute();
  const resolvedTitle = computed(
    () => toDisplayText(unref(pageTitle)) || resolveRoutePageTitle(route)
  );
  const isDirty = computed(() => Boolean(unref(options.dirty)));

  const stop = watch(
    [resolvedTitle, isDirty],
    ([title, dirty]) => {
      document.title = formatPageTitle(title, { dirty });
    },
    { immediate: true }
  );

  onBeforeUnmount(stop);

  return {
    resolvedTitle
  };
}
